/**
 * Nice Scale Algorithm to force fixed number of intervals
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @param {number} tickCount - Expected number of ticks (e.g. 6 for 5 intervals)
 * @returns {Object} - { min, max, step, ticks }
 */
export const calculateNiceScale = (min, max, tickCount = 6) => {
    if (min === max) {
        if (min === 0) return { min: 0, max: 10, step: 2, ticks: [0, 2, 4, 6, 8, 10] };
        max = Math.ceil(min * 1.2);
        min = 0;
    }

    const intervalCount = tickCount - 1;
    const roughStep = (max - min) / intervalCount;
    const niceStep = getNiceStep(roughStep);

    const niceMin = Math.floor(min / niceStep) * niceStep;
    const niceMax = niceMin + niceStep * intervalCount;

    const ticks = [];
    for (let i = 0; i < tickCount; i++) {
        const tickValue = niceMin + i * niceStep;
        ticks.push(parseFloat(tickValue.toPrecision(12)));
    }

    return { min: niceMin, max: niceMax, step: niceStep, ticks };
};

/**
 * Get nice step value for scale calculation
 * @param {number} roughStep - Rough step value
 * @returns {number} - Nice step value
 */
export const getNiceStep = (roughStep) => {
    const exponent = Math.floor(Math.log10(roughStep));
    const fraction = roughStep / Math.pow(10, exponent);

    let niceFraction;
    if (fraction <= 1) niceFraction = 1;
    else if (fraction <= 2) niceFraction = 2;
    else if (fraction <= 2.5) niceFraction = 2.5;
    else if (fraction <= 5) niceFraction = 5;
    else niceFraction = 10;

    return niceFraction * Math.pow(10, exponent);
};

/**
 * Generate Y-axis labels with absolute positions
 * @param {Object} scale - Scale object with min, max, and ticks
 * @param {number} chartHeight - Total height of the chart
 * @param {number} topOffset - Padding from top
 * @param {number} bottomOffset - Padding from bottom
 * @returns {Array} - Array of { value, position } objects
 */
export const generateYLabelsWithPositions = (scale, chartHeight, topOffset, bottomOffset) => {
    const { min, max, ticks } = scale;
    const availableHeight = chartHeight - topOffset - bottomOffset;

    return ticks.slice().reverse().map((value) => {
        const percentage = (max - value) / (max - min);
        const position = topOffset + percentage * availableHeight;
        return { value, position: Math.round(position * 100) / 100 };
    });
};
